import {
  useCallback,
  useMemo,
  useState,
} from 'react';

import {
  memberService,
} from '../../member/services/memberService';

import {
  getMyTournamentReservations,
  getTournaments,
  reserveTournament,
} from '../services/tournament.service';

import type {
  TournamentListItem,
} from '../types/tournament.types';


export type TournamentViewItem = {
  id: number;
  title: string;
  imageUrl: string;

  maxParticipants: number;
  reservedParticipants: number;

  location: string;

  date: string;
  startTime: string;

  buyIn: number;
};


const formatDateKey = (
  year: number,
  month: number,
  day: number,
): string =>
  `${year}-${String(
    month + 1,
  ).padStart(
    2,
    '0',
  )}-${String(
    day,
  ).padStart(
    2,
    '0',
  )}`;


const mapTournament = (
  item: TournamentListItem,
): TournamentViewItem => {

  const date =
    new Date(
      item.tournamentTime,
    );


  return {
    id:
      item.tournamentId,

    title:
      item.tournamentTitle,

    imageUrl:
      item.tournamentPoster,

    maxParticipants:
      item.tournamentEntry,

    reservedParticipants:
      item.participantCount,

    location:
      item.tournamentField,

    date:
      formatDateKey(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
      ),

    startTime:
      new Intl.DateTimeFormat(
        'ko-KR',
        {
          hour:
            '2-digit',

          minute:
            '2-digit',

          hour12:
            false,
        },
      ).format(
        date,
      ),

    buyIn:
      item.tournamentPrice,
  };
};


export const useTournamentSchedule =
  () => {

    const [
      tournaments,
      setTournaments,
    ] =
      useState<
        TournamentViewItem[]
      >([]);


    const [
      isLoading,
      setIsLoading,
    ] =
      useState(
        false,
      );


    const [
      errorMessage,
      setErrorMessage,
    ] =
      useState(
        '',
      );


    const [
      currentUserId,
      setCurrentUserId,
    ] =
      useState<
        number | null
      >(
        null,
      );


    /**
     * =========================================================
     * 대회별 현재 USER 예약 상태
     *
     * {
     *   1: true,
     *   4: true,
     *   10: true
     * }
     *
     * true:
     * 이미 해당 대회 참가자 목록에 존재
     * =========================================================
     */
    const [
      reservationStatus,
      setReservationStatus,
    ] =
      useState<
        Record<
          number,
          boolean
        >
      >({});


    const [
      reservationLoadingId,
      setReservationLoadingId,
    ] =
      useState<
        number | null
      >(
        null,
      );


    /**
     * =========================================================
     * 현재 사용자 조회
     * =========================================================
     */
    const loadCurrentUser =
      useCallback(
        async (): Promise<void> => {

          try {

            const member =
              await memberService
                .getMyInfo();


            setCurrentUserId(
              member.id,
            );

          } catch (
            error
          ) {

            console.error(
              '현재 사용자 조회 실패:',
              error,
            );

          }
        },
        [],
      );


    /**
     * =========================================================
     * 내 기존 예약 상태 조회
     *
     * 새로고침 / 재진입 후에도
     * 예약완료 상태를 복원합니다.
     * =========================================================
     */
    const loadMyReservations =
      useCallback(
        async (): Promise<void> => {

          try {

            const reservations =
              await getMyTournamentReservations();


            const nextStatus:
              Record<
                number,
                boolean
              > =
                {};


            reservations.forEach(
              (
                reservation,
              ) => {

                nextStatus[
                  reservation.tournamentId
                ] =
                  true;

              },
            );


            setReservationStatus(
              nextStatus,
            );

          } catch (
            error
          ) {

            console.error(
              '내 대회 예약 조회 실패:',
              error,
            );


            /**
             * 예약 조회 실패했다고 기존 상태를
             * 무조건 초기화하지 않습니다.
             *
             * 직전에 예약한 상태가 있다면
             * 유지하는 편이 안전합니다.
             */

          }
        },
        [],
      );


    /**
     * =========================================================
     * 월별 대회 조회
     *
     * 대회 목록 +
     * 현재 USER 예약 상태를 동시에 조회
     * =========================================================
     */
    const loadMonthlyTournaments =
      useCallback(
        async (
          month: string,
        ): Promise<void> => {

          try {

            setIsLoading(
              true,
            );


            setErrorMessage(
              '',
            );


            const [
              tournamentData,
              reservationData,
            ] =
              await Promise.all([
                getTournaments(
                  month,
                ),

                getMyTournamentReservations(),
              ]);


            setTournaments(
              tournamentData.map(
                mapTournament,
              ),
            );


            const nextStatus:
              Record<
                number,
                boolean
              > =
                {};


            reservationData.forEach(
              (
                reservation,
              ) => {

                nextStatus[
                  reservation.tournamentId
                ] =
                  true;

              },
            );


            setReservationStatus(
              nextStatus,
            );

          } catch (
            error
          ) {

            console.error(
              '월별 대회 조회 실패:',
              error,
            );


            setErrorMessage(
              '대회 일정을 불러오지 못했습니다.',
            );


            setTournaments(
              [],
            );

          } finally {

            setIsLoading(
              false,
            );

          }
        },
        [],
      );


    /**
     * =========================================================
     * USER 대회 예약
     * =========================================================
     */
    const reserve =
      useCallback(
        async (
          tournament:
            TournamentViewItem,
        ): Promise<void> => {

          /**
           * 이미 예약된 대회라면
           * 프론트에서도 중복 요청 차단
           */
          if (
            reservationStatus[
              tournament.id
            ]
          ) {
            return;
          }


          /**
           * 동일 대회 예약 요청이
           * 이미 진행 중이면 차단
           */
          if (
            reservationLoadingId !=
            null
          ) {
            return;
          }


          try {

            setReservationLoadingId(
              tournament.id,
            );


            await reserveTournament(
              tournament.id,
            );


            /**
             * 예약 성공 즉시
             * 버튼을 예약완료 상태로 변경
             */
            setReservationStatus(
              (
                previous,
              ) => ({
                ...previous,

                [tournament.id]:
                  true,
              }),
            );


            /**
             * 화면상 예약 인원 +1
             */
            setTournaments(
              (
                previous,
              ) =>
                previous.map(
                  (
                    item,
                  ) =>
                    item.id ===
                    tournament.id
                      ? {
                          ...item,

                          reservedParticipants:
                            Math.min(
                              item.maxParticipants,

                              item.reservedParticipants +
                                1,
                            ),
                        }
                      : item,
                ),
            );

          } finally {

            setReservationLoadingId(
              null,
            );

          }
        },
        [
          reservationLoadingId,
          reservationStatus,
        ],
      );


    /**
     * =========================================================
     * 날짜별 대회 Map
     * =========================================================
     */
    const tournamentMap =
      useMemo(
        () => {

          const map =
            new Map<
              string,
              TournamentViewItem[]
            >();


          tournaments.forEach(
            (
              tournament,
            ) => {

              const existing =
                map.get(
                  tournament.date,
                ) ??
                [];


              map.set(
                tournament.date,
                [
                  ...existing,
                  tournament,
                ],
              );

            },
          );


          return map;

        },
        [
          tournaments,
        ],
      );


    return {

      tournaments,

      tournamentMap,

      isLoading,

      errorMessage,

      currentUserId,

      reservationStatus,

      reservationLoadingId,

      loadCurrentUser,

      loadMonthlyTournaments,

      loadMyReservations,

      reserve,

    };
  };